<template>
    <div class="space-y-6">
        <!-- Header with Process Selector -->
        <div class="bg-white rounded-lg shadow p-6 border border-gray-200 relative">
            <div class="absolute top-6 right-6">
                <div class="flex items-center">
                    <label for="process-selector" class="mr-2 text-sm font-medium text-gray-700">Forecasting
                        Process:</label>
                    <select
                            id="process-selector"
                            v-model="selectedProcessId"
                            @change="handleProcessChange"
                            class="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-64 p-2.5"
                            :disabled="loading"
                    >
                        <option v-if="loading" value="">Loading processes...</option>
                        <option v-else-if="processes.length === 0" value="">No processes available</option>
                        <option v-for="process in processes" :key="process.id" :value="process.id">
                            {{ process.name }} ({{ formatProcessDate(process.start_date) }})
                        </option>
                    </select>
                    <div v-if="loading" class="ml-2">
                        <svg class="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                             viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                    stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </div>
                </div>
            </div>

            <!-- Upload Excel Template Section -->
            <h3 class="text-xl font-semibold mb-4">1. Upload Reporting Calendar</h3>

            <!-- Notification for existing calendar -->
            <div v-if="existingCalendar"
                 class="mb-4 bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded flex items-center justify-between">
                <div class="flex items-center">
                    <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                         xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                    </svg>
                    <span>A calendar has already been uploaded for this forecasting process: <strong>{{
                        existingCalendar.name
                        }}</strong></span>
                </div>
            </div>

            <div class="mb-4">
                <input
                        type="file"
                        id="excel-template"
                        accept=".xlsx, .xls, .csv"
                        @change="handleFileUpload"
                        class="hidden"
                        ref="fileInput"
                />

                <button
                        @click="triggerFileUpload"
                        :class="[
            existingCalendar
              ? 'bg-transparent text-blue-700 border border-blue-700 hover:bg-blue-50'
              : 'bg-blue-700 text-white hover:bg-blue-800',
            'px-4 py-2 rounded-md flex items-center transition-colors'
          ]"
                        :disabled="isUploading"
                >
                    <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                         xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                    </svg>
                    <span v-if="existingCalendar">Replace Reporting Calendar</span>
                    <span v-else>Upload Reporting Calendar</span>
                </button>
            </div>

            <!-- Upload progress indicator -->
            <div v-if="isUploading"
                 class="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4 flex items-center">
                <svg class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none"
                     viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Processing file...</span>
            </div>

            <!-- Success message after upload -->
            <div v-if="uploadSuccess"
                 class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex items-center justify-between">
                <div class="flex items-center">
                    <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                         xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>{{ uploadSuccess }}</span>
                </div>
                <button
                        @click="clearUploadSuccess"
                        class="text-green-700 hover:text-green-900 focus:outline-none"
                        aria-label="Close notification"
                >
                    <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                         xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>
            </div>

            <!-- Display filename after upload -->
            <div v-if="selectedFile"
                 class="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded mb-4 flex items-center justify-between">
                <div class="flex items-center">
                    <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                         xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                    <span>{{ selectedFile.name }}</span>
                </div>
                <button
                        @click="clearSelectedFile"
                        class="text-blue-700 hover:text-blue-900 focus:outline-none"
                        aria-label="Close notification"
                >
                    <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                         xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>
            </div>
            <!-- Error message for problems -->
            <div v-if="uploadError"
                 class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
                <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                     xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span>{{
                    uploadError instanceof Object && 'message' in uploadError ? uploadError.message : uploadError
                    }}</span>
            </div>

            <!-- Parse Button -->
            <button
                    v-if="selectedFile && !scheduleData"
                    @click="showParseConfirmation"
                    class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50"
            >
                Parse Calendar
            </button>
        </div>

        <div class="space-y-6">
            <!-- Upcoming Meetings Section -->
            <div class="flex gap-5">
                <div class="space-y-6 flex-grow">
                    <div v-if="scheduleData" class="bg-white rounded-lg shadow p-6 border border-gray-200">
                        <h3 class="text-xl font-semibold mb-4">2. Relevant Meetings of Forecasting Period</h3>
                        <p class="text-muted-foreground mb-4">Your scheduled meetings for the current forecasting
                            period</p>

                        <div v-if="currentPeriodMeetings.length > 0" class="space-y-4">
                            <div v-for="(item, index) in currentPeriodMeetings" :key="index"
                                 class="p-4 border rounded-lg">
                                <div class="flex justify-between items-start">
                                    <div>
                                        <h4 class="font-medium text-lg">{{ item.title }}</h4>
                                        <p v-if="item.comments" class="text-gray-600 mt-1">{{ item.comments }}</p>
                                    </div>
                                    <div class="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                                        {{
                                        item.type === 'meeting' ? 'Meeting' : item.type === 'process' ? 'Process' : 'Other'
                                        }}
                                    </div>
                                </div>
                                <div class="mt-3 flex items-center text-sm text-gray-500">
                                    <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                    </svg>
                                    {{ formatDate(item.startDate) }}
                                    <span v-if="item.endDate"> - {{ formatDate(item.endDate) }}</span>
                                </div>
                            </div>
                        </div>

                        <EmptyState
                                v-else
                                icon="document"
                                title="No Upcoming Meetings"
                                message="There are no meetings scheduled for the current forecasting period."
                                iconClass="text-gray-400"
                        />
                    </div>

                    <!-- Saved Drafts Section -->
                    <div v-if="scheduleData" class="bg-white shadow-md rounded-lg p-6">
                        <h3 class="text-xl font-bold mb-4">3. Saved Drafts</h3>

                        <div v-if="savedDrafts.length > 0" class="overflow-x-auto">
                            <table class="min-w-full bg-white">
                                <thead>
                                <tr>
                                    <th class="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                        Subject
                                    </th>
                                    <th class="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                        Created At
                                    </th>
                                    <th class="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="draft in savedDrafts" :key="draft.id">
                                    <td class="py-2 px-4 border-b border-gray-200">
                                        {{ draft.subject }}
                                    </td>
                                    <td class="py-2 px-4 border-b border-gray-200">
                                        {{ formatDate(draft.createdAt) }}
                                    </td>
                                    <td class="py-2 px-4 border-b border-gray-200">
                                        <button
                                                @click="loadEmailDraft(draft.id)"
                                                class="text-blue-500 hover:text-blue-700 mr-2"
                                        >
                                            Load
                                        </button>
                                        <button
                                                @click="showDeleteConfirmation(draft.id, draft.subject)"
                                                class="text-red-500 hover:text-red-700"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>

                        <EmptyState
                                v-else
                                icon="document"
                                title="No Saved Drafts"
                                message="You haven't saved any email drafts yet. After creating a draft, click 'Save Draft' to save it here."
                                iconClass="text-gray-400"
                        />
                    </div>
                </div>
                <div class="flex">
                    <ChatbotInterface v-if="userData && userData?.id"
                                      :userData="userData"
                                      :environment="currentEnv"
                                      :userId="userData?.id"
                                      class="h-full"
                                      style=" max-height: 800px; min-width: 600px; max-width: 600px; position: sticky; top: 0;"/>
                </div>
            </div>

            <!-- Email Draft Preview Section -->
            <div v-if="emailDraft" class="bg-white shadow-md rounded-lg p-6">
                <h3 class="text-xl font-bold mb-4">4. Email Draft Preview</h3>

                <div class="mb-4">
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="email-subject">
                        Subject
                    </label>
                    <input
                            type="text"
                            id="email-subject"
                            v-model="emailDraft.subject"
                            class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    />
                </div>

                <div class="mb-4">
                    <label class="block text-gray-700 text-sm font-bold mb-2">
                        HTML Content
                    </label>
                    <div class="border rounded p-4 bg-gray-50 h-64 overflow-auto relative">
                        <div v-html="processedHtmlContent"></div>
                        <button
                                @click="showPreviewModal = true"
                                class="absolute top-2 right-2 bg-white p-1 rounded-md shadow hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                title="View in full screen">
                            <svg class="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"></path>
                            </svg>
                        </button>
                    </div>
                </div>

                <div class="mb-4">
                    <label class="block text-gray-700 text-sm font-bold mb-2">
                        Text Content
                    </label>
                    <textarea
                            v-model="emailDraft.textContent"
                            class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-32"
                            readonly
                    ></textarea>
                </div>

                <!-- Recipient input for email draft -->
                <div class="mb-4">
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="draft-recipient">
                        Recipient Emails
                    </label>
                    <input
                            id="draft-recipient"
                            v-model="emailDraftRecipient"
                            type="email"
                            placeholder="Enter recipient email addresses (comma separated)"
                            class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    />
                </div>

                <!-- CC input for email draft -->
                <div class="mb-4">
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="draft-cc">
                        CC Emails
                    </label>
                    <input
                            id="draft-cc"
                            v-model="emailDraftCC"
                            type="email"
                            placeholder="Enter CC email addresses (comma separated)"
                            class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    />
                </div>

                <div class="flex justify-end space-x-3">
                    <button
                            @click="clearEmailDraft"
                            class="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                    >
                        Clear
                    </button>

                    <button
                            @click="saveEmailDraft"
                            class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                    >
                        Save Draft
                    </button>

                    <button
                            @click="sendEmailDraft"
                            :disabled="!emailDraft || !emailDraftRecipient || isEmailDraftSending"
                            class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50"
                    >
                        <span v-if="isEmailDraftSending">Sending...</span>
                        <span v-else>Send Draft</span>
                    </button>
                </div>

                <div v-if="emailDraftStatus.message"
                     :class="['mt-4 p-4 rounded', emailDraftStatus.isError ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700']">
                    {{ emailDraftStatus.message }}
                </div>
            </div>
        </div>

        <!-- Vertical layout for all sections -->

        <!-- Confirmation Modal -->
        <ConfirmationModal
                v-model:isOpen="showConfirmDialog"
                title="Process File"
                :message="`Do you want to process the file '${selectedFile?.name || ''}'?`"
                confirmButtonText="Process"
                cancelButtonText="Cancel"
                iconType="info"
                @confirm="parseFile"
        />

        <!-- Email Preview Modal -->
        <EmailPreviewModal
                v-model:isOpen="showPreviewModal"
                :subject="emailDraft?.subject || ''"
                :htmlContent="emailDraft?.htmlContent || ''"
                :textContent="emailDraft?.textContent || ''"
        />

        <!-- Delete Confirmation Modal -->
        <ConfirmationModal
                v-model:isOpen="showDeleteModal"
                title="Delete Email Draft"
                :message="`Are you sure you want to delete the email draft '${draftToDeleteSubject}'?`"
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
                confirmButtonClass="bg-red-600 hover:bg-red-700 text-white focus:ring-red-500"
                iconType="warning"
                @confirm="confirmDelete"
        />

        <!-- Missing Meeting Types Modal -->
        <ConfirmationModal
                v-model:isOpen="showMissingTypesModal"
                title="Missing Meeting Types Detected"
                :message="getMissingTypesMessage()"
                confirmButtonText="Acknowledge"
                cancelButtonText="Close"
                confirmButtonClass="bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500"
                iconType="warning"
                @confirm="showMissingTypesModal = false"
                @cancel="showMissingTypesModal = false"
        />
    </div>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, PropType, reactive, ref, Ref} from 'vue';
import {ConfirmationModal, EmptyState} from '@/shared/ui';
import {ForecastingParserService} from '@/features/forecasting/services/forecastingParser';
import {ForecastSchedule} from '@/features/forecasting/models/types';
import {AppError, ErrorType, withErrorHandling} from '@/utils/errorHandling';
import EmailPreviewModal from '@/features/email-generation/components/EmailPreviewModal.vue';
import {
    CalendarParserService,
    EmailStorageService,
    EmailTemplateService,
    LoggingService
} from '@/features/email-generation/services';
import {EmailDraft} from '@/features/email-generation/models/types';
import {LogCategory, logger} from '@/services/logger';
import {emailService} from '@/services/emailService';
import {useForecastingProcess} from '@/composables/useDatabase';
import {useStorage} from '@/composables/useStorage';
import {DOCUMENT_STATUS, REPORT_TYPE} from '@/types/document';
import {supabase} from '@/services/supabase';
import {StorageBucket} from '@/types/storage';
import {systemConfigService} from '@/features/system-config';
import {ChatbotEnv} from "@/types/chatbotEnv";
import {BusinessRole} from "@/types/businessRole";
import {UserData} from "@/components/Chatbot.vue";
import ChatbotInterface from './chatbot/ChatbotInterface.vue';

export default defineComponent({
    name: 'ForecastingPCOCalendar',
    components: {
        ChatbotInterface,
        ConfirmationModal,
        EmptyState,
        EmailPreviewModal
    },
    props: {
        userData: {
            type: Object as PropType<UserData>,
            default: () => ({
                name: 'User',
                avatar: null,
                business_role: BusinessRole.PLANNER
            })
        }
    },
    setup() {
        const currentEnv = ChatbotEnv.FORECASTING_CALENDAR;
        // Forecasting process selection
        const {
            processes,
            loading,
            fetchProcesses
        } = useForecastingProcess();

        const selectedProcessId = ref<string>('');

        // File upload refs
        const fileInput = ref<HTMLInputElement | null>(null);
        const selectedFile = ref<File | null>(null);
        const uploadError = ref<Error | AppError | null>(null);
        const uploadSuccess = ref<string | null>(null);
        const isUploading = ref<boolean>(false);

        // Forecast schedule data
        const scheduleData = ref<ForecastSchedule | null>(null);

        // Track if a calendar already exists for the current process
        const existingCalendar = ref<{ id: string; name: string } | null>(null);

        // Confirmation dialog
        const showConfirmDialog = ref<boolean>(false);

        // Service for parsing Excel file
        const forecastingParser = new ForecastingParserService();

        // Email services
        const calendarParser = new CalendarParserService();
        const emailTemplate = new EmailTemplateService();
        const emailStorage = new EmailStorageService();
        const loggingService = new LoggingService();

        // Email draft state
        const emailDraft = ref<EmailDraft | null>(null);
        const savedDrafts = ref<EmailDraft[]>([]);
        const showPreviewModal = ref<boolean>(false);
        const showDeleteModal = ref<boolean>(false);
        const draftToDeleteId = ref<string | null>(null);
        const draftToDeleteSubject = ref<string>('');
        
        // Missing meeting types state
        const showMissingTypesModal = ref<boolean>(false);
        const missingMeetingTypes = ref<string[]>([]);
        
        // Function to check for missing meeting types
        const checkMissingMeetingTypes = () => {
            if (emailDraft.value && emailDraft.value.meetingInfo) {
                // Get the meeting dates from the email template service
                const specificMeetings = emailTemplate.findSpecificMeetings(emailDraft.value.meetingInfo);
                
                // Check for missing meeting types
                const { hasMissingTypes, missingTypes } = emailTemplate.checkMissingMeetingTypes(specificMeetings);
                
                // Update state and show modal if needed
                missingMeetingTypes.value = missingTypes;
                if (hasMissingTypes) {
                    showMissingTypesModal.value = true;
                    
                    // Log the missing meeting types
                    logger.warn('Missing meeting types detected', LogCategory.GENERAL, {
                        missingTypes,
                        meetingInfo: emailDraft.value.meetingInfo.title
                    });
                }
            }
        };
        
        // Helper function to generate the message for the missing types modal
        const getMissingTypesMessage = () => {
            if (missingMeetingTypes.value.length === 0) {
                return 'All required meeting types are present.';
            }
            
            return `The following meeting types are missing from your calendar:\n\n${
                missingMeetingTypes.value.map(type => `• ${type}`).join('\n')
            }\n\nPlease consider adding these meeting types to your calendar for a complete forecasting process.`;
        };

        // Email sending state
        const emailDraftRecipient = ref<string>('');
        const emailDraftCC = ref<string>('');
        const isEmailDraftSending = ref<boolean>(false);
        const emailDraftStatus = reactive({
            message: '',
            isError: false
        });

        // Function to fetch meeting attendees from system configuration
        const fetchMeetingAttendees = async () => {
            try {
                // Fetch board meeting attendees from system configuration
                const configData = await systemConfigService.getAllConfig();
                const boardMeetingAttendeesConfig = configData.find(item => item.property === 'board_meeting_attendees');
                const attendees = boardMeetingAttendeesConfig?.value || [];

                // Set the recipient email to the list of board meeting attendees
                if (attendees.length > 0) {
                    emailDraftRecipient.value = attendees.join(', ');
                    logger.info('Using board meeting attendees as email recipients', LogCategory.GENERAL, {
                        attendeesCount: attendees.length
                    });
                } else {
                    // Fallback to empty if no attendees are configured
                    emailDraftRecipient.value = '';
                    logger.warn('No board meeting attendees configured', LogCategory.GENERAL);
                }
            } catch (error) {
                logger.error('Error fetching board meeting attendees', LogCategory.GENERAL, {error});
                // Fallback to empty recipient if there's an error
                emailDraftRecipient.value = '';
            }
        };

        // Filter meetings to only show those in the current forecasting period
        const currentPeriodMeetings = computed(() => {
            if (!scheduleData.value || !scheduleData.value.items) {
                return [];
            }

            // Get the current forecasting process
            const currentProcess = processes.value.find(p => p.id === selectedProcessId.value);
            if (!currentProcess) {
                return scheduleData.value.items;
            }

            // Get the start and end dates of the current forecasting period
            const periodStart = new Date(currentProcess.start_date);
            const periodEnd = currentProcess.end_date ? new Date(currentProcess.end_date) : new Date(9999, 11, 31); // Far future date if no end date

            // Filter meetings to only include those within the current period
            return scheduleData.value.items.filter(item => {
                const meetingDate = new Date(item.startDate);
                return meetingDate >= periodStart && meetingDate <= periodEnd;
            });
        });

        // Process HTML content to fix logo display
        const processedHtmlContent = computed(() => {
            if (!emailDraft.value) return '';
            // Inline SVG for Fraport logo
            const fraportLogoSvg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:svg="http://www.w3.org/2000/svg" width="100" height="50" viewBox="0 0 284.25531 122.08274" version="1.1" id="svg8"><g transform="translate(72.99477,-76.279161)" id="layer1"><path id="path847" d="m 116.87023,197.9848 c 0,-0.48219 0.99424,-6.08414 2.09317,-11.79373 0.47054,-2.44475 1.24539,-6.58813 1.72185,-9.2075 0.47648,-2.61938 1.28173,-6.95325 1.78943,-9.63084 0.5077,-2.67758 0.98922,-5.32077 1.07002,-5.87375 l 0.14694,-1.00541 h 2.93399 2.93402 l -0.10871,0.58208 c -0.58698,3.14257 -0.68616,3.76303 -0.61322,3.83596 0.0463,0.0463 0.7777,-0.61135 1.62541,-1.46138 1.85716,-1.86225 3.00706,-2.62445 4.79147,-3.17592 2.71742,-0.83983 5.50162,-0.40024 7.17618,1.13301 3.57243,3.27094 3.73281,11.10599 0.35321,17.25442 -1.62626,2.95858 -3.4616,4.86941 -5.98746,6.23368 -3.58616,1.93697 -7.03605,1.8451 -10.05215,-0.26768 -0.66804,-0.46796 -1.29337,-0.85084 -1.38962,-0.85084 -0.0962,0 -0.23909,0.50006 -0.31745,1.11125 -0.0784,0.61119 -0.51143,3.01625 -0.96239,5.34458 -0.45093,2.32834 -0.9805,5.1144 -1.17678,6.19125 l -0.35689,1.95792 h -2.83551 c -2.57852,0 -2.83551,-0.0342 -2.83551,-0.3771 z" style="fill:#003f8a;fill-opacity:1;stroke-width:0.211667" /></g></svg>`;

            // Convert SVG to data URL
            const svgDataUrl = `data:image/svg+xml;base64,${btoa(fraportLogoSvg)}`;
            const bodyOpenTag: string = '<body>';
            // Replace CID reference with data URL
            return emailDraft.value.htmlContent.replace(
                'src="cid:fraport-logo"',
                `src="${svgDataUrl}"`
            ).slice(emailDraft.value.htmlContent.indexOf(bodyOpenTag) + bodyOpenTag.length, emailDraft.value.htmlContent.indexOf('</body>'));
        });

        // File upload methods
        const triggerFileUpload = () => {
            fileInput.value?.click();
        };

        // Get the current user
        const getCurrentUser = async () => {
            const {data} = await supabase.auth.getUser();
            return data?.user;
        };

        const handleFileUpload = async (event: Event) => {
            const input = event.target as HTMLInputElement;
            if (input.files && input.files.length > 0) {
                const file = input.files[0];

                // Reset upload status
                uploadError.value = null;
                uploadSuccess.value = null;
                isUploading.value = true;
                scheduleData.value = null;

                await withErrorHandling(
                    async () => {
                        // Check if a forecasting process is selected
                        if (!selectedProcessId.value) {
                            throw new Error('Please select a forecasting process before uploading a calendar');
                        }

                        // Get current user
                        const user = await getCurrentUser();
                        if (!user) {
                            throw new Error('User not authenticated');
                        }

                        // Initialize storage
                        const {uploadFile} = useStorage();

                        // Generate a unique filename with timestamp
                        const timestamp = new Date().getTime();
                        const fileName = `${timestamp}-${file.name}`;

                        // Upload file to Supabase storage using the more robust approach
                        await uploadFile(
                            StorageBucket.CALENDARS,
                            fileName,
                            file,
                            {customFileName: `Reporting Calendar - ${file.name}`, createDocumentEntry: false}
                        );

                        // The file is already saved to the database by the uploadFile function
                        // Now create a document entry referencing the file

                        // First, get the file ID from the database
                        const {data: fileData, error: fileError} = await supabase
                            .from('files')
                            .select('id')
                            .eq('name', `Reporting Calendar - ${file.name}`)
                            .limit(1)
                            .single();

                        if (fileError) {
                            throw fileError;
                        }

                        // Create document entry referencing the file and forecasting process
                        const {data: document, error: documentError} = await supabase
                            .from('documents')
                            .insert({
                                name: `Reporting Calendar - ${file.name}`,
                                description: `Reporting Calendar for forecasting process ${selectedProcessId.value}`,
                                status: DOCUMENT_STATUS.CREATING,
                                created_by: user.id,
                                forecast_id: selectedProcessId.value,
                                file_id: fileData.id,
                                report_type: REPORT_TYPE.CAL
                            })
                            .select()
                            .single();

                        if (documentError) {
                            throw documentError;
                        }

                        logger.info('Calendar document created', LogCategory.GENERAL, {
                            documentId: document.id,
                            fileId: fileData.id,
                            forecastId: selectedProcessId.value
                        });

                        // Save file and show success message
                        selectedFile.value = file;
                        uploadSuccess.value = `File "${file.name}" successfully uploaded and saved to database.`;
                    },
                    uploadError as Ref<Error | AppError | null>,
                    ErrorType.STORAGE,
                    'Error uploading file'
                );

                // Reset upload status
                isUploading.value = false;
            }
        };

        // Shows the confirmation dialog
        const showParseConfirmation = () => {
            showConfirmDialog.value = true;
        };

        // Parses the uploaded Excel file
        const parseFile = async () => {
            if (!selectedFile.value) {
                uploadError.value = {
                    type: ErrorType.VALIDATION,
                    message: 'Please select a file first'
                };
                return;
            }

            // Reset upload status
            uploadError.value = null;
            isUploading.value = true;

            await withErrorHandling(
                async () => {
                    // Parse Excel file for forecasting
                    const forecastData = await forecastingParser.parseExcelFile(selectedFile.value!);

                    // Parse Excel file for email generation
                    const meetingInfo = await calendarParser.parseExcelFile(selectedFile.value!);

                    // Get current user
                    const user = await getCurrentUser();
                    const userId = user ? user.id : 'current-user';

                    // Get the current forecasting process
                    const currentProcess = processes.value.find(p => p.id === selectedProcessId.value);

                    // Add forecasting process information to meetingInfo
                    if (currentProcess) {
                        meetingInfo.forecastProcess = {
                            id: currentProcess.id,
                            name: currentProcess.name,
                            startDate: new Date(currentProcess.start_date),
                            endDate: currentProcess.end_date ? new Date(currentProcess.end_date) : undefined
                        };

                        console.log('Added forecasting process to meetingInfo:', meetingInfo.forecastProcess);
                    }

                    // Save forecast data
                    scheduleData.value = forecastData;

                    // Generate email draft
                    emailDraft.value = emailTemplate.generateEmailDraft(meetingInfo, userId);

                    // Log the email generation
                    loggingService.logEmailGeneration(emailDraft.value, userId, 'create');

                    // Check for missing meeting types
                    checkMissingMeetingTypes();

                    // Fetch meeting attendees to populate the recipient field
                    await fetchMeetingAttendees();

                    // Show success message
                    uploadSuccess.value = `File "${selectedFile.value!.name}" successfully processed.`;
                },
                uploadError as Ref<Error | AppError | null>,
                ErrorType.VALIDATION,
                'Error parsing Excel file'
            );

            // Reset upload status
            isUploading.value = false;

            // Close confirmation dialog
            showConfirmDialog.value = false;
        };

        // Email draft methods
        const loadSavedDrafts = () => {
            savedDrafts.value = emailStorage.listEmailDrafts();
        };

        const loadEmailDraft = async (id: string) => {
            const draft = emailStorage.getEmailDraft(id);
            if (draft) {
                emailDraft.value = draft;

                // Convert meetingInfo to ForecastSchedule format and set scheduleData
                if (draft.meetingInfo && draft.meetingInfo.activities) {
                    const scheduleItems = draft.meetingInfo.activities.map(activity => ({
                        title: activity.title,
                        startDate: activity.startDate,
                        endDate: activity.endDate,
                        comments: activity.comments,
                        type: 'meeting' as 'meeting' | 'process' | 'other'
                    }));

                    // Add the meeting itself if available
                    if (draft.meetingInfo.title) {
                        scheduleItems.unshift({
                            title: draft.meetingInfo.title,
                            startDate: draft.meetingInfo.meetingDate,
                            endDate: draft.meetingInfo.endDate,
                            comments: draft.meetingInfo.comments,
                            type: 'meeting' as 'meeting' | 'process' | 'other'
                        });
                    }

                    scheduleData.value = {
                        items: scheduleItems,
                        period: draft.meetingInfo.title || 'Unknown Period'
                    };
                }

                // Log the load action
                loggingService.logEmailGeneration(draft, 'current-user', 'load');

                // Check for missing meeting types
                checkMissingMeetingTypes();

                // Fetch meeting attendees to populate the recipient field
                await fetchMeetingAttendees();
            }
        };

        const saveEmailDraft = () => {
            if (emailDraft.value) {
                // Save the email draft
                emailStorage.saveEmailDraft(emailDraft.value);

                // Log the save action
                loggingService.logEmailGeneration(emailDraft.value, 'current-user', 'save');

                // Reload saved drafts
                loadSavedDrafts();
            }
        };

        // Show delete confirmation modal
        const showDeleteConfirmation = (id: string, subject: string) => {
            draftToDeleteId.value = id;
            draftToDeleteSubject.value = subject;
            showDeleteModal.value = true;
        };

        // Confirm delete action
        const confirmDelete = () => {
            if (draftToDeleteId.value) {
                const draft = emailStorage.getEmailDraft(draftToDeleteId.value);
                if (draft) {
                    // Log the delete action
                    loggingService.logEmailGeneration(draft, 'current-user', 'delete');

                    // Delete the draft
                    emailStorage.deleteEmailDraft(draftToDeleteId.value);

                    // Reload saved drafts
                    loadSavedDrafts();
                }

                // Reset delete confirmation state
                draftToDeleteId.value = null;
                draftToDeleteSubject.value = '';
                showDeleteModal.value = false;
            }
        };

        const clearEmailDraft = () => {
            emailDraft.value = null;
            scheduleData.value = null; // Also reset scheduleData so the Parse button is shown again
            // Keep the selectedFile so the user can parse the same file again

            // Clear the recipient and CC fields
            emailDraftRecipient.value = '';
            emailDraftCC.value = '';
        };

        // Clear the upload success message
        const clearUploadSuccess = () => {
            uploadSuccess.value = null;
        };

        // Clear the selected file
        const clearSelectedFile = () => {
            selectedFile.value = null;
            // Also clear the upload success message
            uploadSuccess.value = null;
        };

        // Send email draft using the email service
        const sendEmailDraft = async () => {
            if (!emailDraft.value) {
                emailDraftStatus.message = 'No email draft available to send';
                emailDraftStatus.isError = true;
                return;
            }

            if (!emailDraftRecipient.value) {
                emailDraftStatus.message = 'Please enter at least one recipient email address';
                emailDraftStatus.isError = true;
                return;
            }

            // Reset status
            emailDraftStatus.message = '';
            emailDraftStatus.isError = false;
            isEmailDraftSending.value = true;

            try {
                const bccRecipients = emailDraftRecipient.value;

                // Get current user's email for the To field
                const {data} = await supabase.auth.getUser();
                const userEmail = data?.user?.email;

                // Use the centralized email service
                const result = await emailService.sendEmail({
                    to: userEmail, // Send to the current user
                    cc: emailDraftCC.value, // CC recipients
                    bcc: bccRecipients, // BCC all the meeting attendees
                    subject: emailDraft.value.subject,
                    body: emailDraft.value.textContent, // Use textContent for plain text body
                    html: emailDraft.value.htmlContent // Use htmlContent for HTML content
                });

                // Set status based on result
                emailDraftStatus.message = result.message;
                emailDraftStatus.isError = !result.success;

                // Log result
                if (result.success) {
                    logger.info('Email draft sent successfully', LogCategory.GENERAL, {
                        to: userEmail,
                        bcc: bccRecipients,
                        subject: emailDraft.value.subject
                    });
                }
            } catch (error) {
                // This should not happen with our implementation, but just in case
                emailDraftStatus.message = error instanceof Error ? error.message : 'An unknown error occurred';
                emailDraftStatus.isError = true;

                logger.error('Error sending email draft', LogCategory.GENERAL, {
                    error,
                    draft: emailDraft.value
                });
            } finally {
                isEmailDraftSending.value = false;
            }
        };

        // Helper function to format dates
        const formatDate = (dateInput: string | Date): string => {
            if (!dateInput) return '';

            const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        };

        // Helper function to format process dates (shorter format)
        const formatProcessDate = (dateInput: string | Date): string => {
            if (!dateInput) return '';

            const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short'
            });
        };

        // Check if a calendar already exists for the selected process and load its data
        const checkExistingCalendar = async () => {
            if (!selectedProcessId.value) return;

            try {
                // Reset states
                existingCalendar.value = null;

                // Clear existing data when switching processes
                clearCalendarData();

                // Force a fresh query with no caching to ensure we get the latest data
                const {data, error} = await supabase
                    .from('documents')
                    .select('id, name, file_id, status')
                    .eq('forecast_id', selectedProcessId.value)
                    .eq('report_type', REPORT_TYPE.CAL)
                    .not('file_id', 'is', null)  // Ensure file_id is not null
                    .limit(1);

                if (error) {
                    logger.error('Error checking for existing calendar', LogCategory.GENERAL, {error});

                    // Try a simpler query without the not-null filter if the first query fails
                    try {
                        const {data: fallbackData, error: fallbackError} = await supabase
                            .from('documents')
                            .select('id, name, file_id, status')
                            .eq('forecast_id', selectedProcessId.value)
                            .eq('report_type', REPORT_TYPE.CAL)
                            .limit(1);

                        if (fallbackError) {
                            logger.error('Fallback query also failed', LogCategory.GENERAL, {error: fallbackError});
                            return;
                        }

                        // If we got data, filter out entries with null file_id manually
                        if (fallbackData && fallbackData.length > 0) {
                            const validData = fallbackData.filter(item => item.file_id !== null);
                            if (validData.length > 0) {
                                // Use the first valid entry
                                logger.info('Found valid calendar using fallback query', LogCategory.GENERAL, {
                                    processId: selectedProcessId.value,
                                    calendarData: validData[0]
                                });

                                // Process the valid data directly
                                if (validData[0].file_id) {
                                    existingCalendar.value = {
                                        id: validData[0].id,
                                        name: validData[0].name
                                    };

                                    logger.info('Existing calendar found for process using fallback', LogCategory.GENERAL, {
                                        processId: selectedProcessId.value,
                                        calendarId: validData[0].id
                                    });

                                    // Fetch the file data to parse it
                                    await loadExistingCalendarData(validData[0].file_id);
                                }
                                return;
                            } else {
                                logger.info('No valid calendar found in fallback query', LogCategory.GENERAL, {
                                    processId: selectedProcessId.value,
                                    dataFound: fallbackData.length,
                                    validDataFound: 0
                                });
                                return;
                            }
                        } else {
                            return;
                        }
                    } catch (fallbackErr) {
                        logger.error('Error in fallback query', LogCategory.GENERAL, {error: fallbackErr});
                        return;
                    }
                }

                // If a calendar exists and has a valid file_id, store it and load its data
                if (data && data.length > 0 && data[0].file_id) {
                    logger.info('Checking calendar data', LogCategory.GENERAL, {
                        processId: selectedProcessId.value,
                        calendarData: data[0]
                    });

                    existingCalendar.value = {
                        id: data[0].id,
                        name: data[0].name
                    };

                    logger.info('Existing calendar found for process', LogCategory.GENERAL, {
                        processId: selectedProcessId.value,
                        calendarId: data[0].id
                    });

                    // Fetch the file data to parse it
                    await loadExistingCalendarData(data[0].file_id);
                } else {
                    logger.info('No valid calendar found for process', LogCategory.GENERAL, {
                        processId: selectedProcessId.value,
                        dataFound: data ? data.length : 0,
                        hasFileId: data && data.length > 0 ? !!data[0].file_id : false
                    });

                    // View is already cleared by clearCalendarData() above
                }
            } catch (err) {
                logger.error('Error checking for existing calendar', LogCategory.GENERAL, {error: err});
                existingCalendar.value = null;
                clearCalendarData();
            }
        };

        // Clear calendar data when switching processes
        const clearCalendarData = () => {
            // Clear schedule data and email draft
            scheduleData.value = null;
            emailDraft.value = null;
            selectedFile.value = null;
            uploadSuccess.value = null;
        };

        // Load and parse the existing calendar file
        const loadExistingCalendarData = async (fileId: string) => {
            try {
                // First get the file details from the database
                const {data: fileData, error: fileError} = await supabase
                    .from('files')
                    .select('*')
                    .eq('id', fileId)
                    .single();

                if (fileError) {
                    logger.error('Error fetching file data', LogCategory.GENERAL, {error: fileError});
                    existingCalendar.value = null; // Reset if we can't find the file
                    return;
                }

                // Log file data for debugging
                logger.info('File data retrieved', LogCategory.GENERAL, {
                    fileId: fileId,
                    fileName: fileData?.name,
                    hasUrl: !!fileData?.url
                });

                // Get the file URL and extract the path
                const fileUrl = fileData.url;

                if (!fileUrl) {
                    logger.error('File URL is missing', LogCategory.GENERAL, {fileId});
                    existingCalendar.value = null; // Reset if URL is missing
                    return;
                }

                const filePath = fileUrl.split('/').pop();

                if (!filePath) {
                    logger.error('Invalid file URL', LogCategory.GENERAL, {url: fileUrl});
                    existingCalendar.value = null; // Reset if path is invalid
                    return;
                }

                // Download the file from storage
                const {downloadFile} = useStorage();
                const fileBlob = await downloadFile(StorageBucket.CALENDARS, filePath);

                if (!fileBlob) {
                    logger.error('Failed to download file', LogCategory.GENERAL, {path: filePath});
                    existingCalendar.value = null; // Reset if download fails
                    return;
                }

                // Convert blob to File object
                const file = new File([fileBlob], fileData.name, {type: fileBlob.type});

                // Parse the file
                logger.info('Parsing existing calendar file', LogCategory.GENERAL, {fileName: fileData.name});

                // Parse Excel file for forecasting
                const forecastData = await forecastingParser.parseExcelFile(file);

                // Parse Excel file for email generation
                const meetingInfo = await calendarParser.parseExcelFile(file);

                // Get current user
                const user = await getCurrentUser();
                const userId = user ? user.id : 'current-user';

                // Get the current forecasting process
                const currentProcess = processes.value.find(p => p.id === selectedProcessId.value);

                // Add forecasting process information to meetingInfo
                if (currentProcess) {
                    meetingInfo.forecastProcess = {
                        id: currentProcess.id,
                        name: currentProcess.name,
                        startDate: new Date(currentProcess.start_date),
                        endDate: currentProcess.end_date ? new Date(currentProcess.end_date) : undefined
                    };

                    console.log('Added forecasting process to meetingInfo:', meetingInfo.forecastProcess);
                }

                // Save forecast data
                scheduleData.value = forecastData;

                // Generate email draft
                emailDraft.value = emailTemplate.generateEmailDraft(meetingInfo, userId);

                // Check for missing meeting types
                checkMissingMeetingTypes();

                // Fetch meeting attendees to populate the recipient field
                await fetchMeetingAttendees();

                logger.info('Successfully parsed existing calendar', LogCategory.GENERAL, {
                    fileName: fileData.name,
                    meetingsCount: forecastData?.items?.length || 0
                });
            } catch (err) {
                logger.error('Error loading existing calendar data', LogCategory.GENERAL, {error: err});
            }
        };

        // Handle process selection change
        const handleProcessChange = async () => {
            logger.info('Forecasting process selected', LogCategory.GENERAL, {
                processId: selectedProcessId.value
            });

            // Reset existing calendar state before checking
            existingCalendar.value = null;

            // Force a small delay to ensure any previous state is cleared
            await new Promise(resolve => setTimeout(resolve, 100));

            // Check if a calendar already exists for this process
            await checkExistingCalendar();
        };

        // Load saved drafts and fetch processes on component mount
        onMounted(async () => {
            loadSavedDrafts();

            // Fetch meeting attendees for the recipient field
            await fetchMeetingAttendees();

            try {
                await fetchProcesses();

                // Select the newest process (they're already sorted by created_at in descending order)
                if (processes.value.length > 0) {
                    // Log all available processes for debugging
                    logger.info('Available forecasting processes', LogCategory.GENERAL, {
                        count: processes.value.length,
                        processes: processes.value.map(p => ({id: p.id, name: p.name}))
                    });

                    selectedProcessId.value = processes.value[0].id;

                    // Reset existing calendar state before checking
                    existingCalendar.value = null;

                    // Force a small delay to ensure any previous state is cleared
                    await new Promise(resolve => setTimeout(resolve, 100));

                    // Check for existing calendar for the initially selected process
                    await checkExistingCalendar();
                }
            } catch (err) {
                logger.error('Error fetching forecasting processes', LogCategory.GENERAL, {error: err});
            }
        });

        return {
            currentEnv,
            // Forecasting process
            processes,
            loading,
            selectedProcessId,
            handleProcessChange,
            formatProcessDate,

            // File upload
            fileInput,
            selectedFile,
            uploadError,
            uploadSuccess,
            isUploading,
            scheduleData,
            showConfirmDialog,
            triggerFileUpload,
            handleFileUpload,
            showParseConfirmation,
            parseFile,
            formatDate,
            existingCalendar,
            currentPeriodMeetings,

            // Email drafts
            emailDraft,
            savedDrafts,
            processedHtmlContent,
            showPreviewModal,
            showDeleteModal,
            draftToDeleteSubject,
            emailDraftRecipient,
            emailDraftCC,
            isEmailDraftSending,
            emailDraftStatus,
            loadEmailDraft,
            saveEmailDraft,
            clearEmailDraft,
            showDeleteConfirmation,
            confirmDelete,
            sendEmailDraft,

            // Notification controls
            clearUploadSuccess,
            clearSelectedFile,
            
            // Missing meeting types
            showMissingTypesModal,
            missingMeetingTypes,
            getMissingTypesMessage,
            checkMissingMeetingTypes
        };
    }
});
</script>
